import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GasPumpIcon, UploadSimpleIcon, MagnifyingGlassIcon, TrashIcon, CalendarBlankIcon, WarningIcon, EyeIcon, EnvelopeIcon, CopyIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { isCanadianState, formatVolume, formatCurrency, formatPricePerUnit } from "@/lib/units";

const PRODUCT_LABELS = { DSL: "Diesel", ULSD: "Diesel", GAS: "Gasoline", DEF: "DEF" };
const VENDOR_TONES = {
  PILOT: "bg-amber-100 text-amber-800",
  BVD: "bg-purple-100 text-purple-800",
  LOVES: "bg-red-100 text-red-800",
  TA: "bg-blue-100 text-blue-800",
  GENERIC: "bg-slate-200 text-slate-700",
};

/**
 * Admin uploads the daily Pilot/OPIS contract price sheet (.xls or .xlsx).
 * Backend parses it, extracts effective date + every site row, then this
 * page shows the prices with state/product filters + sort.
 */
export default function FuelPrices() {
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ prices: [], available_dates: [], effective_from: null });
  const [dateFilter, setDateFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [productFilter, setProductFilter] = useState("all");
  const [vendorFilter, setVendorFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("your_price");
  const [includeStale, setIncludeStale] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mailbox, setMailbox] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/integrations/email-inbound/config");
        setMailbox(data.fuel_mailbox || "");
      } catch { /* ignore — non-admin drivers or config not yet set */ }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort });
      if (dateFilter) params.set("date", dateFilter);
      if (stateFilter !== "all") params.set("state", stateFilter);
      if (productFilter !== "all") params.set("product", productFilter);
      if (vendorFilter !== "all") params.set("vendor", vendorFilter);
      if (search) params.set("search", search);
      if (includeStale) params.set("include_stale", "true");
      const [d, s] = await Promise.all([
        api.get(`/fuel/prices?${params}`),
        api.get(`/fuel/prices/stats${dateFilter ? `?date=${dateFilter}` : ""}`),
      ]);
      setData(d.data);
      setStats(s.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [dateFilter, stateFilter, productFilter, vendorFilter, search, sort, includeStale]);

  useEffect(() => { load(); }, [load]);

  const onFile = async (files) => {
    const file = files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post("/fuel/price-sheets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${res.rows_inserted} rows for ${res.effective_from}${res.rows_replaced ? ` (replaced ${res.rows_replaced})` : ""}`);
      setDateFilter("");
      setIncludeStale(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const deleteSheet = async (sheet_id, eff_from) => {
    if (!window.confirm(`Delete the price sheet for ${eff_from}?`)) return;
    try {
      await api.delete(`/fuel/price-sheets/${sheet_id}`);
      toast.success("Sheet deleted");
      setDateFilter("");
      await load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const states = useMemo(() => stats?.states || [], [stats]);
  const vendors = useMemo(() => stats?.vendors || [], [stats]);

  // Per-product price terciles → 3 tiers (green cheapest, yellow mid, red priciest).
  // Grouped by product so cheap DEF doesn't skew diesel colouring.
  const tierThresholds = useMemo(() => {
    const byProd = {};
    for (const p of data.prices) {
      if (p.your_price == null) continue;
      (byProd[p.product] ||= []).push(p.your_price);
    }
    const out = {};
    for (const [prod, arr] of Object.entries(byProd)) {
      const s = [...arr].sort((a, b) => a - b);
      const q = (f) => s[Math.min(s.length - 1, Math.floor(f * (s.length - 1)))];
      out[prod] = { t1: q(1 / 3), t2: q(2 / 3), n: s.length };
    }
    return out;
  }, [data.prices]);

  const priceTier = (p) => {
    const th = tierThresholds[p.product];
    if (!th || p.your_price == null || th.n < 3) return null;
    if (p.your_price <= th.t1) return "green";
    if (p.your_price <= th.t2) return "yellow";
    return "red";
  };
  const TIER_CLS = {
    green: "bg-emerald-100 text-emerald-800",
    yellow: "bg-amber-100 text-amber-800",
    red: "bg-red-100 text-red-800",
  };
  const TIER_DOT = { green: "bg-emerald-500", yellow: "bg-amber-500", red: "bg-red-500" };

  return (
    <div data-testid="fuel-prices-page">
      <PageHeader eyebrow="Fuel program" title="Fuel prices" subtitle="Upload Pilot/OPIS daily contract sheet · browse today's cheapest stops across the network." />

      <div className="p-8 space-y-6">
        {/* Upload dropzone */}
        <div className="bg-white border border-slate-300 rounded-sm p-6" data-testid="fuel-upload-card">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-12 h-12 rounded-sm bg-emerald-600 text-white flex items-center justify-center">
              <GasPumpIcon size={22} weight="bold" />
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="section-eyebrow mb-0.5">/// Daily upload</div>
              <h3 className="font-display text-lg font-black tracking-tight">Upload today's price sheet</h3>
              <div className="text-xs text-slate-500 font-mono mt-0.5">Accepts .xls / .xlsx / .pdf (Pilot, BVD, Loves, TA, or any vendor rate sheet)</div>
            </div>
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".xls,.xlsx,.pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf"
                className="hidden"
                onChange={(e) => onFile(e.target.files)}
                data-testid="fuel-upload-input"
              />
              <div className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 h-10 rounded-sm text-sm font-bold cursor-pointer">
                <UploadSimpleIcon size={16} weight="bold" />
                {uploading ? "Uploading…" : "Choose file"}
              </div>
            </label>
          </div>

          {data.available_dates?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="section-eyebrow mb-2">/// Recent uploads</div>
              <div className="flex flex-wrap gap-2">
                {data.available_dates.slice(0, 8).map((d) => (
                  <button
                    key={d.effective_from}
                    onClick={() => setDateFilter(d.effective_from)}
                    className={`text-xs font-mono px-3 py-1.5 rounded-full border ${dateFilter === d.effective_from ? "bg-blue-600 text-white border-blue-600" : "bg-slate-50 border-slate-200 hover:bg-slate-100"}`}
                    data-testid={`fuel-date-${d.effective_from}`}
                    title={`${d.row_count} rows`}
                  >
                    <CalendarBlankIcon size={10} weight="bold" className="inline mr-1" />
                    {d.effective_from}
                  </button>
                ))}
              </div>
            </div>
          )}

          {mailbox && (
            <div className="mt-4 pt-4 border-t border-slate-100" data-testid="fuel-inbox-block">
              <div className="section-eyebrow mb-2">/// Or forward the vendor email</div>
              <div className="bg-slate-900 text-white rounded-sm px-4 py-3 flex items-center gap-3 flex-wrap">
                <EnvelopeIcon size={18} weight="bold" className="text-emerald-400 flex-shrink-0" />
                <div className="flex-1 min-w-[220px]">
                  <div className="font-mono text-sm font-bold break-all">{mailbox}</div>
                  <div className="text-[11px] text-slate-400 font-mono mt-0.5">
                    Forward your Pilot / BVD / any-vendor daily rate email to this address — the attachment is parsed automatically. Same sheet re-forwarded = replaces prior rows for that date.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { navigator.clipboard.writeText(mailbox); toast.success("Mailbox copied"); }}
                  className="bg-white text-slate-900 hover:bg-slate-100 px-3 py-1.5 rounded-sm text-xs font-bold flex items-center gap-1"
                  data-testid="fuel-inbox-copy"
                >
                  <CopyIcon size={12} weight="bold" /> Copy
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Stale warning — today has no sheet uploaded yet */}
        {data.stale && !dateFilter && (
          <div className="bg-amber-50 border-2 border-amber-400 rounded-sm p-4 flex items-start gap-3" data-testid="stale-warning">
            <WarningIcon size={22} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-display font-black text-base tracking-tight text-amber-900">
                No fuel prices for today ({data.today})
              </div>
              <div className="text-sm text-amber-800 mt-0.5">
                {data.latest_effective_from
                  ? `Latest sheet on file is from ${data.latest_effective_from} — ${data.days_stale} day${data.days_stale !== 1 ? "s" : ""} old. Rates from that sheet are hidden by default. Upload today's rate sheet above, or view yesterday's rates temporarily.`
                  : "No rate sheets have ever been uploaded. Upload one above to get started."}
              </div>
              {data.latest_effective_from && (
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setIncludeStale(true)}
                    className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-sm bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="show-stale"
                  >
                    <EyeIcon size={12} weight="bold" />
                    Show yesterday's rates anyway
                  </button>
                  <button
                    type="button"
                    onClick={() => setDateFilter(data.latest_effective_from)}
                    className="text-xs font-bold px-3 py-1.5 rounded-sm bg-white border border-amber-300 text-amber-800 hover:bg-amber-50"
                  >
                    Browse {data.latest_effective_from}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Showing stale banner (yesterday's rates being displayed with warning) */}
        {data.stale && includeStale && !dateFilter && (
          <div className="bg-amber-100 border border-amber-300 rounded-sm px-4 py-2 flex items-center gap-2 text-xs" data-testid="stale-active-banner">
            <WarningIcon size={14} weight="fill" className="text-amber-700" />
            <span className="text-amber-900 font-bold">
              Showing rates from {data.effective_from} ({data.days_stale} day{data.days_stale !== 1 ? "s" : ""} old). Upload today's sheet to refresh.
            </span>
            <button onClick={() => setIncludeStale(false)} className="ml-auto text-amber-700 hover:text-amber-900 underline">Hide</button>
          </div>
        )}

        {/* Historical browse banner */}
        {dateFilter && data.effective_from && (
          <div className="bg-slate-100 border border-slate-300 rounded-sm px-4 py-2 flex items-center gap-2 text-xs" data-testid="historical-banner">
            <CalendarBlankIcon size={14} weight="fill" className="text-slate-600" />
            <span className="text-slate-800 font-bold">Browsing historical sheet for {data.effective_from}.</span>
            <button onClick={() => setDateFilter("")} className="ml-auto text-blue-700 hover:text-blue-900 underline">Back to today</button>
          </div>
        )}

        {/* Stat tiles */}
        {stats && stats.count > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="fuel-stats">
            <StatTile label="Sites" value={stats.count} />
            <StatTile label="Cheapest" value={stats.your_price_min != null ? `$${stats.your_price_min.toFixed(3)}` : "—"} tone="emerald" />
            <StatTile label="Avg price" value={stats.your_price_avg != null ? `$${stats.your_price_avg.toFixed(3)}` : "—"} />
            <StatTile label="Highest" value={stats.your_price_max != null ? `$${stats.your_price_max.toFixed(3)}` : "—"} tone="red" />
            <StatTile label="Avg savings / gal" value={stats.savings_avg != null ? `$${stats.savings_avg.toFixed(3)}` : "—"} tone="emerald" />
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border border-slate-300 rounded-sm p-4 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlassIcon size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search city or site #" className="pl-9 rounded-sm h-9 text-sm" data-testid="fuel-search" />
          </div>
          <Select value={stateFilter} onValueChange={setStateFilter}>
            <SelectTrigger className="h-9 w-32 rounded-sm text-xs" data-testid="fuel-state"><SelectValue placeholder="State" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All states</SelectItem>
              {states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={vendorFilter} onValueChange={setVendorFilter}>
            <SelectTrigger className="h-9 w-32 rounded-sm text-xs" data-testid="fuel-vendor"><SelectValue placeholder="Vendor" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All vendors</SelectItem>
              {vendors.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={productFilter} onValueChange={setProductFilter}>
            <SelectTrigger className="h-9 w-32 rounded-sm text-xs" data-testid="fuel-product"><SelectValue placeholder="Product" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All products</SelectItem>
              <SelectItem value="DSL">Diesel</SelectItem>
              <SelectItem value="GAS">Gasoline</SelectItem>
              <SelectItem value="DEF">DEF</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-9 w-40 rounded-sm text-xs" data-testid="fuel-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="your_price">Cheapest first</SelectItem>
              <SelectItem value="your_price_desc">Most expensive</SelectItem>
              <SelectItem value="savings">Biggest savings</SelectItem>
              <SelectItem value="city">City A→Z</SelectItem>
            </SelectContent>
          </Select>
          {dateFilter && (
            <Button variant="outline" size="sm" className="rounded-sm h-9" onClick={() => setDateFilter("")}>Clear date</Button>
          )}
          {data.effective_from && (
            <div className="text-xs font-mono text-slate-500">
              Effective: <span className="font-bold text-slate-900">{data.effective_from}</span>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-300 rounded-sm overflow-hidden" data-testid="fuel-prices-table">
          {data.prices.length > 0 && !loading && (
            <div className="px-4 py-2 border-b border-slate-200 flex items-center gap-4 text-[11px] font-mono text-slate-600" data-testid="fuel-tier-legend">
              <span className="uppercase tracking-wider font-bold text-slate-500">Price tier (per product):</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Cheapest</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Mid</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Most expensive</span>
            </div>
          )}
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-500 font-mono">LOADING…</div>
          ) : data.prices.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              {data.effective_from
                ? "No sites match those filters."
                : "No price sheets uploaded yet. Upload today's .xls above to get started."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="fleet-table text-xs">
                <thead>
                  <tr>
                    <th>Vendor</th>
                    <th>Site</th>
                    <th>Name</th>
                    <th>City</th>
                    <th>ST</th>
                    <th>Prod</th>
                    <th>Total cost</th>
                    <th>Retail</th>
                    <th>Your price</th>
                    <th>Savings / gal</th>
                  </tr>
                </thead>
                <tbody>
                  {data.prices.map((p) => {
                    const region = isCanadianState(p.state) ? "CA" : "US";
                    const tier = priceTier(p);
                    return (
                    <tr key={p.id} data-testid={`fuel-price-${p.site}`}>
                      <td>
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${VENDOR_TONES[p.vendor] || VENDOR_TONES.GENERIC}`}>
                          {p.vendor || "—"}
                        </span>
                      </td>
                      <td className="font-mono font-bold">#{p.site}</td>
                      <td className="text-slate-700">{p.name || "—"}</td>
                      <td>{p.city}</td>
                      <td className="font-mono">
                        {p.state}
                        {region === "CA" && <span className="ml-1 text-[9px] bg-red-100 text-red-800 px-1 rounded font-bold">🇨🇦</span>}
                      </td>
                      <td className="font-mono">{PRODUCT_LABELS[p.product] || p.product}</td>
                      <td className="font-mono tabular-nums" title={region === "CA" ? "Converted from US$/gal" : ""}>
                        {p.total_cost != null ? formatPricePerUnit(p.total_cost, region, 4) : "—"}
                      </td>
                      <td className="font-mono tabular-nums text-slate-500">
                        {p.retail_price != null ? formatPricePerUnit(p.retail_price, region) : "—"}
                      </td>
                      <td className="font-mono tabular-nums font-bold">
                        {p.your_price != null ? (
                          <span
                            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm ${tier ? TIER_CLS[tier] : "text-emerald-700"}`}
                            data-testid={`fuel-tier-${p.site}`}
                            title={tier === "green" ? "Cheapest tier" : tier === "yellow" ? "Mid-priced" : tier === "red" ? "Most expensive tier" : ""}
                          >
                            {tier && <span className={`w-2 h-2 rounded-full ${TIER_DOT[tier]}`} />}
                            {formatPricePerUnit(p.your_price, region, 4)}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="font-mono tabular-nums font-bold">
                        {p.savings != null ? formatPricePerUnit(p.savings, region, 4) : "—"}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Sheet history + delete */}
        {data.available_dates?.length > 0 && (
          <div className="bg-white border border-slate-300 rounded-sm" data-testid="fuel-history">
            <div className="px-4 py-3 border-b border-slate-200">
              <div className="section-eyebrow mb-0.5">/// History</div>
              <h3 className="font-display text-base font-bold tracking-tight">Uploaded sheets</h3>
            </div>
            <table className="fleet-table text-xs">
              <thead>
                <tr><th>Effective</th><th>Vendor</th><th>Account</th><th>Rows</th><th></th></tr>
              </thead>
              <tbody>
                {data.available_dates.map((d) => (
                  <tr key={`${d.effective_from}-${d.vendor}`}>
                    <td className="font-mono font-bold">{d.effective_from}</td>
                    <td>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${VENDOR_TONES[d.vendor] || VENDOR_TONES.GENERIC}`}>
                        {d.vendor || "—"}
                      </span>
                    </td>
                    <td className="text-slate-700">{d.account_name || "—"}</td>
                    <td className="font-mono tabular-nums">{d.row_count}</td>
                    <td>
                      <button
                        onClick={() => deleteSheet(d.id, d.effective_from)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete sheet"
                        data-testid={`fuel-delete-${d.effective_from}-${d.vendor}`}
                      >
                        <TrashIcon size={14} weight="bold" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, value, tone = "slate" }) {
  const map = { emerald: "text-emerald-700", red: "text-red-700", slate: "text-slate-900" };
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className={`font-display font-black text-2xl tracking-tighter tabular-nums ${map[tone]}`}>{value}</div>
      <div className="font-mono text-[10px] uppercase text-slate-500 tracking-wider font-bold mt-0.5">{label}</div>
    </div>
  );
}
